import React, { ReactNode, useEffect } from "react";
import { BrowserRouter, Route, Switch, useHistory, useLocation } from "react-router-dom";
import {
    Affiliate,
    ALLOWED_NAVIGATION_ROUTES,
    AllowedNavigationRoute,
    Coupon,
    LineItemTemplate,
    LineItemType,
    Permission,
    Source,
} from "types";
import { wasLastLoginOAuth } from "../auth/authMethod";
import { EmbeddedAppRoot } from "../embeddedApps/EmbeddedAppRoot";
import * as hostRpc from "../embeddedApps/hostRpc";
import { usePushDrawerOffset } from "../embeddedApps/usePushDrawerOffset";
import { useAccount } from "../hooks/useAccount";
import { AppStorePage } from "../pages/AppStorePage";
import { ContactPage } from "../pages/Customers/ContactPage";
import { CreateMessageTemplatePage } from "../pages/Customers/CreateMessageTemplatePage";
import { CustomerSettingsPage } from "../pages/Customers/CustomerSettingsPage";
import { DigitalWaiversPage } from "../pages/Customers/DigitalWaiversPage";
import { DigitalWaiversSettingsPage } from "../pages/Customers/DigitalWaiversSettingsPage";
import { MessageTemplateListPage } from "../pages/Customers/MessageTemplateListPage";
import { DashboardPage } from "../pages/DashboardPage";
import { AgentCommissionsPage } from "../pages/Distribution/Agent/AgentCommissionsPage";
import { AgentPage } from "../pages/Distribution/Agent/AgentPage";
import { AgentStatementPage } from "../pages/Distribution/Agent/AgentStatementPage";
import { EditAgentPage } from "../pages/Distribution/Agent/EditAgentPage";
import { EditTermsTemplatePage } from "../pages/Distribution/Agent/EditTermsTemplatePage";
import { TermsTemplateListPage } from "../pages/Distribution/TermsTemplateListPage";
import { EditPurchasePage } from "../pages/EditPurchasePage";
import { EmbeddedAppPage } from "../pages/EmbeddedAppPage";
import { EquipmentListPage } from "../pages/EquipmentListPage";
import { EquipmentSettingsPage } from "../pages/EquipmentSettingsPage";
import { GratuityReportPage } from "../pages/GratuityReportPage";
import { HomePage } from "../pages/HomePage";
import { KycCompletedPage } from "../pages/KycCompletedPage";
import { LoginPage } from "../pages/LoginPage";
import { LoginSkeleton } from "../pages/LoginSkeleton";
import { AbandonedBookingRecoveryPage } from "../pages/Marketing/AbandonedBookingRecoveryPage";
import { ConversionTrackingPage } from "../pages/Marketing/ConversionTrackingPage";
import { CouponPage } from "../pages/Marketing/CouponPage";
import { EditCouponPage } from "../pages/Marketing/EditCouponPage";
import { TelemetryModulePage } from "../pages/Marketing/TelemetryModulePage";
import { TelemetryPage } from "../pages/Marketing/TelemetryPage";
import { XolaBotPage } from "../pages/Marketing/XolaBotPage";
import { PageNotFound } from "../pages/PageNotFound";
import { PluginCanvasPage } from "../pages/PluginCanvasPage";
import { ExperienceListPage } from "../pages/Product/ExperienceListPage";
import { ExperiencePage } from "../pages/Product/ExperiencePage";
import { GiftGlobalSettingsPage } from "../pages/Product/GiftGlobalSettingsPage";
import { GiftOfferListPage } from "../pages/Product/GiftOfferListPage";
import { GiftOfferPage } from "../pages/Product/GiftOfferPage";
import { MerchandiseInventoryPage } from "../pages/Product/MerchandiseInventoryPage";
import { MerchandiseListPage } from "../pages/Product/MerchandiseListPage";
import { MerchandisePage } from "../pages/Product/MerchandisePage";
import { PackageListPage } from "../pages/Product/PackageListPage";
import { PackagePage } from "../pages/Product/PackagePage";
import { ProductGlobalSettingsPage } from "../pages/Product/ProductGlobalSettingsPage";
import { SelectExperiencePage } from "../pages/Product/SelectExperiencePage";
import { PurchaseItemPage } from "../pages/PurchaseItemPage";
import { AnalyticsPage } from "../pages/Reports/AnalyticsPage";
import { CustomerAnalyticsPage } from "../pages/Reports/CustomerAnalyticsPage";
import { CustomReportListPage } from "../pages/Reports/CustomReportListPage";
import { CustomReportPage } from "../pages/Reports/CustomReportPage";
import { DisputesPage } from "../pages/Reports/DisputesPage";
import { EarningsReportPage } from "../pages/Reports/EarningsReportPage";
import { GiftReportPage } from "../pages/Reports/GiftReportPage";
import { MerchandiseLedgerReportPage } from "../pages/Reports/MerchandiseLedgerReportPage";
import { PayoutDetailsPage } from "../pages/Reports/PayoutDetailsPage";
import { PayoutPage } from "../pages/Reports/PayoutPage";
import { XolaBotEarningsReportPage } from "../pages/Reports/XolaBotEarningsReportPage";
import { EditEquipmentPage } from "../pages/Resources/EditEquipmentPage";
import { EditGuidePage } from "../pages/Resources/EditGuidePage";
import { GuideManagementPage } from "../pages/Resources/GuideManagementPage";
import { GuideSettingsPage } from "../pages/Resources/GuideSettingsPage";
import { SettingsPage } from "../pages/SettingsPage";
import { SignInSkeleton } from "../pages/SignInSkeleton";
import { SupportPage } from "../pages/SupportPage";
import { XsitePage } from "../pages/XsitePage";
import { useTrackGtmPageView } from "../snippets/googleTagManager";
import { useSnippets } from "../snippets/useSnippets";
import { useAccountStore } from "../stores/accountStore";
import { OAuthSessionStatus, useAuthStore } from "../stores/authStore";
import { AppCalloutBanner } from "./AppCallout/AppCalloutBanner";
import { AccountSelector } from "./Common/AccountSelector";
import { LocalizationSetup } from "./Common/LocalizationSetup";
import { OpenAccountModal } from "./Common/OpenAccountModal";
import { ScrollToTop } from "./Common/ScrollToTop";
import { DistributionProfilePage } from "./Distribution/DistributionProfilePage";
import { PartnerListPage } from "./Distribution/Partner/PartnerListPage";
import { PartnerProfilePage } from "./Distribution/PartnerProfilePage";
import { LegalWallModal } from "./LegalWall/LegalWallModal";
import { useXolaMobileApps } from "./MobileApps/XolaMobileAppsContext";
import { MembershipGlobalSettingsPage } from "./Product/Membership/GlobalSettings/MembershipGlobalSettingsPage";
import { ProtectedRoute } from "./ProtectedRoute";
import { SessionExpiredModal } from "./SessionExpired/SessionExpiredModal";
import { MandatoryInformationCheck } from "./Settings/MandatoryInformationCheck";
import { AutoLogout } from "@/components/AutoLogout/AutoLogout";
import { PurchaseDrawerManager } from "@/components/Dashboard/DashboardTable/PurchaseDrawerManager";
import { TranslationsPage } from "@/components/Settings/Translations/TranslationsPage";
import { useForms } from "@/hooks/resources/useForms";
import { useExperiences, useProducts } from "@/hooks/resources/useProduct";
import { usePurchases } from "@/hooks/resources/usePurchases";
import { useUnits } from "@/hooks/resources/useUnits";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { useOpenAccountHotkey } from "@/hooks/useOpenAccountHotkey";
import { useAutoFetchInstallations, useAutoFetchPlugins } from "@/hooks/usePlugins";
import { EmailHistoryPage } from "@/pages/Debug/EmailHistoryPage";
import { MessageDeliveryPage } from "@/pages/Debug/MessageDeliveryPage";
import { MenuSidebar } from "@/pages/Menu/MenuSidebar";
import { MembershipListPage } from "@/pages/Product/MembershipListPage";
import { MembershipPage } from "@/pages/Product/MembershipPage";
import { PartnerPackageListPage } from "@/pages/Product/PartnerPackageListPage";
import { PartnerPackagePage } from "@/pages/Product/PartnerPackagePage";
import { canApplyCoupons, canShowAffiliates, useInitializeGlobalStore } from "@/stores/globalStore";
import { useOpenAccountStore } from "@/stores/openAccountStore";

export const Router = () => {
    const { data: account } = useAccount();
    const { seller } = useAccountStore.getState();
    const authStatus = useAuthStore((state) => state.status);

    if (authStatus === OAuthSessionStatus.INITIALIZING) {
        return wasLastLoginOAuth() ? <LoginSkeleton /> : <SignInSkeleton />;
    }

    if (authStatus === OAuthSessionStatus.EXCHANGING_CODE) {
        return <LoginSkeleton />;
    }

    if (!account) {
        return <LoginPage />;
    }

    if (account && !seller) {
        return <AccountSelector />;
    }

    return (
        <>
            <LoadGlobalData />
            <LoadProducts />
            <LoadEssentials />
            <AnimatedRouter>
                <RouteChangeTracking />
                <LocalizationSetup />
                <ScrollToTop />
                {/* Snippets is a component so we can load this only after the user has logged in */}
                <Snippets />
                <Switch>
                    <Route exact path="/">
                        <HomePage />
                    </Route>

                    <Route exact path="/dashboard/:timeslotId?">
                        <DashboardPage />
                    </Route>

                    <ProtectedRoute exact path="/customers/" permission={Permission.CUSTOMERS_VIEW}>
                        <ContactPage />
                    </ProtectedRoute>

                    <ProtectedRoute
                        exact
                        path="/customers/contacts/:customerId?"
                        permission={Permission.CUSTOMERS_VIEW}
                    >
                        <ContactPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/customers/settings" permission={Permission.CUSTOMERS_SETTINGS_VIEW}>
                        <CustomerSettingsPage />
                    </ProtectedRoute>

                    <Route exact path="/customers/messageTemplates/create">
                        <CreateMessageTemplatePage />
                    </Route>

                    <Route exact path="/customers/messageTemplates/:id?">
                        <MessageTemplateListPage />
                    </Route>

                    <Route exact path="/customers/digitalWaivers">
                        <DigitalWaiversPage />
                    </Route>

                    <Route exact path="/customers/digitalWaivers/settings">
                        <DigitalWaiversSettingsPage />
                    </Route>

                    <Route exact path="/marketing/coupons">
                        <CouponPage />
                    </Route>

                    <Route exact path="/marketing/coupons/create">
                        <EditCouponPage />
                    </Route>

                    <Route exact path="/marketing/coupons/:id">
                        <EditCouponPage />
                    </Route>

                    <Route exact path="/marketing/coupons/:id/:section">
                        <EditCouponPage />
                    </Route>

                    <Route exact path="/marketing/conversionTracking/:section">
                        <ConversionTrackingPage />
                    </Route>

                    <Route exact path="/marketing/serverSideTracking">
                        <TelemetryPage />
                    </Route>

                    <Route exact path="/marketing/serverSideTracking/modules/create">
                        <TelemetryModulePage />
                    </Route>

                    <Route exact path="/marketing/serverSideTracking/modules/:id/edit">
                        <TelemetryModulePage />
                    </Route>

                    <Route exact path="/marketing/xolabot">
                        <XolaBotPage />
                    </Route>

                    <Route exact path="/marketing/xolabot/:section">
                        <XolaBotPage />
                    </Route>

                    <Route exact path="/resources/guides">
                        <GuideManagementPage />
                    </Route>

                    <Route exact path="/resources/guides/settings/:section">
                        <GuideSettingsPage />
                    </Route>

                    <ProtectedRoute
                        exact
                        path="/resources/guides/create"
                        permission={Permission.MODIFY_RESOURCES_GUIDE}
                    >
                        <EditGuidePage />
                    </ProtectedRoute>

                    <Route exact path="/resources/guides/:id/edit">
                        <EditGuidePage />
                    </Route>

                    <Route exact path="/resources/guides/:id/edit/:section">
                        <EditGuidePage />
                    </Route>

                    <Route exact path="/abandonedBookingRecovery">
                        <AbandonedBookingRecoveryPage />
                    </Route>

                    <Route exact path="/apps/:appId?">
                        <AppStorePage />
                    </Route>

                    <Route exact path="/settings">
                        <SettingsPage />
                    </Route>

                    <Route exact path="/settings/apps/:appId">
                        <AppStorePage route="settings" />
                    </Route>

                    <ProtectedRoute
                        exact
                        path="/settings/languages/:locale/translations"
                        permission={Permission.MULTI_LANGUAGE_SUPPORT_VIEW}
                    >
                        <TranslationsPage />
                    </ProtectedRoute>

                    <Route exact path="/settings/:section">
                        <SettingsPage />
                    </Route>

                    <Route exact path="/settings/:section/:action">
                        <SettingsPage />
                    </Route>

                    <Route exact path="/settings/:section/:purchaseRuleId/:purchaseRuleLinkId">
                        <SettingsPage />
                    </Route>

                    <Route exact path="/settings/payments/kycCompleted">
                        <KycCompletedPage />
                    </Route>

                    <Route path="/pluginCanvas/:id/:section?">
                        <PluginCanvasPage />
                    </Route>

                    <Route exact path="/embeddedApps/:id">
                        <EmbeddedAppPage />
                    </Route>

                    <ProtectedRoute exact path="/xsite" permission={Permission.XSITE_VIEW}>
                        <XsitePage />
                    </ProtectedRoute>

                    <Route exact path="/resources/equipment">
                        <EquipmentListPage />
                    </Route>

                    <Route exact path="/resources/equipment/create">
                        <EditEquipmentPage />
                    </Route>

                    <Route exact path="/resources/equipment/settings">
                        <EquipmentSettingsPage />
                    </Route>

                    <Route exact path="/resources/equipment/:id/:step">
                        <EditEquipmentPage />
                    </Route>

                    <ProtectedRoute exact path="/emails" permission={Permission.XOLA_ADMIN}>
                        <EmailHistoryPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/emails/:id" permission={Permission.XOLA_ADMIN}>
                        <EmailHistoryPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/messages" permission={Permission.XOLA_ADMIN}>
                        <MessageDeliveryPage />
                    </ProtectedRoute>

                    <Route exact path="/support">
                        <SupportPage />
                    </Route>

                    <Route exact path="/cart/init">
                        <SelectExperiencePage />
                    </Route>

                    <Route exact path="/cart">
                        <EditPurchasePage />
                    </Route>

                    <ProtectedRoute exact path="/purchases" permission={Permission.PURCHASES_VIEW}>
                        <PurchaseItemPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/purchases/:id" permission={Permission.PURCHASES_VIEW}>
                        <PurchaseItemPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/purchases/:id/:itemId" permission={Permission.PURCHASES_VIEW}>
                        <PurchaseItemPage />
                    </ProtectedRoute>

                    <Route exact path="/reports">
                        <AnalyticsPage />
                    </Route>

                    <Route exact path="/reports/analytics">
                        <AnalyticsPage />
                    </Route>

                    <Route exact path="/reports/customerAnalytics">
                        <CustomerAnalyticsPage />
                    </Route>

                    <Route exact path="/reports/earningsReport">
                        <EarningsReportPage />
                    </Route>

                    <Route exact path="/reports/xolabotEarningsReport">
                        <XolaBotEarningsReportPage />
                    </Route>

                    <Route exact path="/reports/gifts">
                        <GiftReportPage />
                    </Route>

                    <Route exact path="/reports/payouts">
                        <PayoutPage />
                    </Route>

                    <Route exact path="/reports/payouts/:id">
                        <PayoutDetailsPage />
                    </Route>

                    <Route exact path="/reports/gratuities">
                        <GratuityReportPage />
                    </Route>

                    <Route exact path="/reports/merchandiseLedger">
                        <MerchandiseLedgerReportPage />
                    </Route>

                    <Route exact path="/reports/disputes">
                        <DisputesPage />
                    </Route>

                    <Route exact path="/products/globalSettings">
                        <ProductGlobalSettingsPage />
                    </Route>

                    <Route exact path="/products/globalSettings/:section">
                        <ProductGlobalSettingsPage />
                    </Route>

                    <Route exact path="/reports/distributionPartnersReconciliation">
                        <AgentCommissionsPage />
                    </Route>

                    <Route exact path="/reports/partnerStatement/:agentId">
                        <AgentStatementPage />
                    </Route>

                    <Route exact path="/products/experiences">
                        <ExperienceListPage />
                    </Route>

                    <Route exact path="/products/experiences/new">
                        <ExperiencePage />
                    </Route>

                    <Route exact path="/products/experiences/:id">
                        <ExperiencePage />
                    </Route>

                    <Route exact path="/products/experiences/:id/:section">
                        <ExperiencePage />
                    </Route>

                    <Route exact path="/products/merchandise">
                        <MerchandiseListPage />
                    </Route>

                    <Route exact path="/products/merchandise/new">
                        <MerchandisePage />
                    </Route>

                    <Route exact path="/products/inventory">
                        <MerchandiseInventoryPage />
                    </Route>

                    <Route exact path="/products/merchandise/:id">
                        <MerchandisePage />
                    </Route>

                    <Route exact path="/products/merchandise/:id/:section">
                        <MerchandisePage />
                    </Route>

                    <Route exact path="/products/packages">
                        <PackageListPage />
                    </Route>

                    <Route exact path="/products/packages/:id/:section?">
                        <PackagePage />
                    </Route>

                    <Route exact path="/products/packages/new">
                        <PackagePage />
                    </Route>

                    <Route exact path="/products/partnerPackages">
                        <PartnerPackageListPage />
                    </Route>

                    <Route exact path="/products/partnerPackages/:id/:section?">
                        <PartnerPackagePage />
                    </Route>

                    <Route exact path="/products/partnerPackages/new">
                        <PartnerPackagePage />
                    </Route>

                    <ProtectedRoute exact path="/products/memberships" permission={Permission.MEMBERSHIPS_VIEW}>
                        <MembershipListPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/products/memberships/new" permission={Permission.MEMBERSHIPS_MODIFY}>
                        <MembershipPage />
                    </ProtectedRoute>

                    <ProtectedRoute
                        exact
                        path="/products/memberships/globalSettings/:section"
                        permission={Permission.MEMBERSHIPS_MODIFY}
                    >
                        <MembershipGlobalSettingsPage />
                    </ProtectedRoute>

                    <ProtectedRoute exact path="/products/memberships/:id" permission={Permission.MEMBERSHIPS_MODIFY}>
                        <MembershipPage />
                    </ProtectedRoute>

                    <ProtectedRoute
                        exact
                        path="/products/memberships/:id/:section"
                        permission={Permission.MEMBERSHIPS_MODIFY}
                    >
                        <MembershipPage />
                    </ProtectedRoute>

                    <ProtectedRoute
                        exact
                        path="/reports/customReports"
                        permission={Permission.REPORTS_CUSTOM_REPORTS_VIEW}
                    >
                        <CustomReportListPage />
                    </ProtectedRoute>

                    <Route exact path="/reports/customReports/new">
                        <CustomReportPage />
                    </Route>

                    <Route exact path="/reports/customReports/:id">
                        <CustomReportPage />
                    </Route>

                    <Route exact path="/products/giftOffers">
                        <GiftOfferListPage />
                    </Route>

                    <Route exact path="/products/giftOffers/create">
                        <GiftOfferPage />
                    </Route>

                    <ProtectedRoute
                        exact
                        path="/products/gifts/globalSettings"
                        permission={Permission.PREFERENCES_GIFT_VIEW}
                    >
                        <GiftGlobalSettingsPage />
                    </ProtectedRoute>

                    <Route exact path="/products/giftOffers/:id">
                        <GiftOfferPage />
                    </Route>

                    <Route exact path="/products/giftOffers/:id/:section">
                        <GiftOfferPage />
                    </Route>

                    <Route exact path="/products/globalSettings/:tab?">
                        <ProductGlobalSettingsPage />
                    </Route>

                    <Route exact path="/distribution/agents">
                        <AgentPage />
                    </Route>

                    <Route exact path="/distribution/agents/create">
                        <EditAgentPage />
                    </Route>

                    <Route exact path="/distribution/agents/:id/:tab?">
                        <EditAgentPage />
                    </Route>

                    <Route exact path="/distribution/termsTemplates">
                        <TermsTemplateListPage />
                    </Route>

                    <Route exact path="/distribution/termsTemplates/create">
                        <EditTermsTemplatePage />
                    </Route>

                    <Route exact path="/distribution/termsTemplates/:id">
                        <EditTermsTemplatePage />
                    </Route>

                    {/* Routes for backward compatibility */}

                    <Route exact path="/analytics">
                        <AnalyticsPage />
                    </Route>

                    <Route exact path="/commission">
                        <AgentPage />
                    </Route>

                    <ProtectedRoute exact path="/analytics/reports" permission={Permission.REPORTS_CUSTOM_REPORTS_VIEW}>
                        <CustomReportListPage />
                    </ProtectedRoute>

                    <Route exact path="/inventory">
                        <EquipmentListPage />
                    </Route>

                    <Route exact path="/settings/userManagement">
                        <SettingsPage />
                    </Route>

                    <ProtectedRoute exact path="/guides" permission={Permission.MODIFY_RESOURCES_GUIDE}>
                        <GuideManagementPage />
                    </ProtectedRoute>

                    <Route exact path="/giftOffers">
                        <GiftOfferListPage />
                    </Route>

                    <Route exact path="/distribution/profile">
                        <DistributionProfilePage />
                    </Route>

                    <Route exact path="/distribution/operators">
                        <PartnerListPage />
                    </Route>

                    <Route exact path="/distribution/operators/:id/:tab?">
                        <PartnerProfilePage />
                    </Route>

                    {/* End backward compatibility routes */}

                    <Route>
                        <PageNotFound />
                    </Route>
                </Switch>
                <PurchaseDrawerManager />
                <LegalWallModal />
                <AppCalloutBanner />
                <SessionExpiredModal />
                <AutoLogout />
            </AnimatedRouter>
        </>
    );
};

const AnimatedRouter = ({ children }: { children: ReactNode }) => {
    return (
        <BrowserRouter>
            <NavigationHandler />
            <EmbeddedAppNavigationHandler />
            <EmbeddedAppRouteTracker />
            <div className="flex h-full w-full">
                <MenuSidebar />
                <MandatoryInformationCheck />
                <PushContentArea>{children}</PushContentArea>
            </div>
            <EmbeddedAppRoot />
            <AccountSwitchByHotKey />
        </BrowserRouter>
    );
};

interface PushContentAreaProps {
    children: ReactNode;
}

const PushContentArea = ({ children }: PushContentAreaProps) => {
    const { leftPx, rightPx } = usePushDrawerOffset();
    return (
        // Margins stay inline: they track the drawer width in pixels, which Tailwind cannot express.
        <div
            className="min-w-0 flex-1 transition-[margin] duration-500 ease-in-out"
            style={{ marginLeft: leftPx, marginRight: rightPx }}
        >
            {children}
        </div>
    );
};

const EmbeddedAppNavigationHandler = () => {
    const history = useHistory();
    // Re-runs if history changes (shouldn't in practice) to keep the navigate
    // callback bound to the current history instance.
    useEffect(() => {
        hostRpc.init();
        hostRpc.setNavigateCallback((route, replace) => {
            replace ? history.replace(route) : history.push(route);
        });
    }, [history]);
    return null;
};

const EmbeddedAppRouteTracker = () => {
    const location = useLocation();
    // Notifies embedded app sessions of route changes so they can update their
    // context and fire the "ui.navigate" event to subscribed apps.
    useEffect(() => {
        hostRpc.notifyRouteChanged(location.pathname);
    }, [location.pathname]);
    return null;
};
const AccountSwitchByHotKey = React.memo(() => {
    const { data } = useAccount();
    const hasMultipleAccounts = (data?.delegators?.length ?? 0) > 1;
    const isOpen = useOpenAccountStore((s) => s.isOpen);
    const hide = useOpenAccountStore((s) => s.hide);

    useOpenAccountHotkey();

    if (!hasMultipleAccounts) {
        return null;
    }

    return <OpenAccountModal isOpen={isOpen} onClose={hide} />;
});

const Snippets = () => {
    useSnippets();
    return null;
};

const RouteChangeTracking = () => {
    useTrackGtmPageView();
    return null;
};

const NavigationHandler = () => {
    const history = useHistory();
    const { mobileAppMessage } = useXolaMobileApps();

    useEffect(() => {
        if (mobileAppMessage?.type === "NAVIGATE" && mobileAppMessage.payload?.to) {
            const targetRoute = mobileAppMessage.payload.to as AllowedNavigationRoute;
            const isStaticRoute = ALLOWED_NAVIGATION_ROUTES.includes(
                targetRoute as typeof ALLOWED_NAVIGATION_ROUTES[number],
            );
            const isDynamicRoute = /^\/?purchases(?:\/[^/]+){2}$/.test(targetRoute);
            if (isStaticRoute || isDynamicRoute) {
                const normalizedRoute = targetRoute.startsWith("/") ? targetRoute : `/${targetRoute}`;
                history.push(normalizedRoute);
            }
        }
    }, [mobileAppMessage, history]);

    return null;
};

const LoadGlobalData = () => {
    useInitializeGlobalStore();
    return null;
};

const LoadProducts = () => {
    const { isFetching, hasMore, isFetchingNextPage, isLoading } = useExperiences();

    useProducts(isFetching || hasMore || isFetchingNextPage || isLoading);

    return null;
};

const LoadEssentials = () => {
    usePurchases();
    useUnits();
    useAutoFetchInstallations({ status: "completed" });
    useAutoFetchPlugins({ status: "approved" });
    useAutoFetchCollection<LineItemTemplate>(["lineItemTemplates"], {
        type: LineItemType.DEMOGRAPHIC,
    });
    useAutoFetchCollection<Affiliate>(["affiliates"], null, {
        enabled: canShowAffiliates(),
    });
    useForms();
    useCollection<Coupon>(
        "coupons",
        {
            limit: 20,
            source: Source.XOLA,
        },
        { enabled: canApplyCoupons() },
    );
    return null;
};
